import { router } from "./router.js";
import { initializeLivestreamComponents, initializeMusic, showRoomLoadingState } from './livestream-components.js';
import { initPWAInstall, refreshBannerLayout, hideBannerWhenLeavingLanding } from './pwa-install.js';
import { isLoggedIn, getBackendUser, authFetch, waitForAuth, openProfileModal, openUserCard, onAuthChange, openAuthModal, getToken } from './auth.js';
import { modalOpen, modalClose } from './modal-state.js';
import { DATE_LOCALE } from './date-locale.js';

const API_URL = import.meta.env.VITE_API_BASE_URL;

const ROOMS_INFO_POLLING_FREQUENCY = 10_000;
const SNAPSHOT_REFRESH_FREQUENCY = 5000;


const escapeHTML = (unsafe = "") =>
  unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const PREVIEW_FALLBACK_POSTER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='640' height='360' viewBox='0 0 640 360'%3E%3Crect width='640' height='360' fill='%23222222'/%3E%3Ctext x='50%25' y='50%25' fill='%23aaaaaa' font-family='Fira Code, monospace' font-size='20' text-anchor='middle'%3Eno preview%3C/text%3E%3C/svg%3E";
const FEEDING_DISABLED_MESSAGE = 'feeding disabled for now';
const comingSoonModal = document.getElementById('comingSoonModal');
const comingSoonModalContent = comingSoonModal ? comingSoonModal.querySelector('.info-modal-content') : null;
const closeComingSoonModalButton = document.getElementById('closeComingSoonModal');
const countdownIntervals = new Set();
const snapshotRefreshIntervals = new Set();
// Store last preview image URLs by stream name
const lastPreviewImages = new Map();
const landingCardMap = new Map();
let landingDataRefreshInterval = null;
let landingRefreshInFlight = false;

initializeMusic();
initPWAInstall();

function lockPortrait() {
  if (document.fullscreenElement) return;
  screen?.orientation?.lock?.('portrait-primary').catch(() => {});
}
lockPortrait();
document.addEventListener('visibilitychange', () => { if (!document.hidden) lockPortrait(); });
window.addEventListener('pageshow', lockPortrait);
const _lockOnGesture = () => {
  lockPortrait();
  document.removeEventListener('click', _lockOnGesture);
  document.removeEventListener('touchstart', _lockOnGesture);
};
document.addEventListener('click', _lockOnGesture);
document.addEventListener('touchstart', _lockOnGesture);

const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  const normalized = `${dateString.replace(' ', 'T')}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return hours > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
};

const clearCountdownTimers = () => {
  countdownIntervals.forEach((intervalId) => clearInterval(intervalId));
  countdownIntervals.clear();
};

const clearSnapshotRefreshTimers = () => {
  snapshotRefreshIntervals.forEach((intervalId) => clearInterval(intervalId));
  snapshotRefreshIntervals.clear();
};

const stopLandingDataRefresh = () => {
  if (landingDataRefreshInterval) {
    clearInterval(landingDataRefreshInterval);
    landingDataRefreshInterval = null;
  }
};

const startLandingDataRefresh = () => {
  stopLandingDataRefresh();
  if (landingCardMap.size === 0) {
    return;
  }
  landingDataRefreshInterval = setInterval(refreshLandingStreamData, ROOMS_INFO_POLLING_FREQUENCY);
};

const openComingSoonModal = () => {
  if (!comingSoonModal || !comingSoonModalContent) return;
  comingSoonModal.classList.add('active');
  comingSoonModalContent.style.display = 'flex';
  modalOpen();
};

const closeComingSoonModal = () => {
  if (!comingSoonModal || !comingSoonModalContent) return;
  comingSoonModal.classList.remove('active');
  comingSoonModalContent.style.display = 'none';
  modalClose();
};

if (closeComingSoonModalButton) {
  closeComingSoonModalButton.addEventListener('click', closeComingSoonModal);
}

if (comingSoonModal) {
  comingSoonModal.addEventListener('click', (event) => {
    if (event.target === comingSoonModal) {
      closeComingSoonModal();
    }
  });
}

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && comingSoonModal?.classList.contains('active')) {
    closeComingSoonModal();
  }
  if (event.key === 'Escape' && legalModal?.classList.contains('active')) {
    closeLegalModal();
  }
});

// ── Legal Modal (Privacy / Cookies / Contact) ──

const legalModal = document.getElementById('legalModal');
const legalModalTitle = document.getElementById('legalModalTitle');
const legalModalBody = document.getElementById('legalModalBody');

const legalContent = {
  cookies: {
    title: 'cookies',
    body: `
      <p>This site uses only <strong>essential cookies</strong> required for the service to function.</p>
      <h3>What cookies we use</h3>
      <ul>
        <li><strong>Authentication cookies</strong> — set by Firebase to keep you signed in.</li>
        <li><strong>Session/preference cookies</strong> — remember local settings like volume level.</li>
      </ul>
      <p>We do not use advertising, tracking, or third-party analytics cookies. No consent banner is needed because we only use strictly necessary cookies.</p>
    `
  },
  contact: {
    title: 'contact',
    body: `
      <p>For any questions, feedback, or data-related requests you can reach us on Telegram: <a href="https://t.me/feed_the_cat_1" target="_blank" rel="noopener noreferrer">@feed_the_cat_1</a>, Instagram: <a href="https://www.instagram.com/feed_the.cat" target="_blank" rel="noopener noreferrer">@feed_the.cat</a>, or email <a href="mailto:support@feed-the-cat.stream">support@feed-the-cat.stream</a></p>
    `
  }
};

function openLegalModal(key) {
  const content = legalContent[key];
  if (!content || !legalModal) return;
  legalModalTitle.textContent = content.title;
  legalModalBody.innerHTML = content.body;
  legalModal.classList.add('active');
  modalOpen();
}

function closeLegalModal() {
  if (!legalModal) return;
  legalModal.classList.remove('active');
  modalClose();
}

document.querySelectorAll('.legal-footer-link').forEach(btn => {
  btn.addEventListener('click', () => {
    if (btn.dataset.legal === 'privacy') {
      router.navigate('/privacy');
    } else {
      openLegalModal(btn.dataset.legal);
    }
  });
});

document.getElementById('legalModalClose')?.addEventListener('click', closeLegalModal);

legalModal?.addEventListener('click', (e) => {
  if (e.target === legalModal) closeLegalModal();
});

const privacyHomeLink = document.getElementById('privacyHomeLink');
if (privacyHomeLink) {
  const goHome = () => router.navigate('/');
  privacyHomeLink.addEventListener('click', goHome);
  privacyHomeLink.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goHome();
    }
  });
}

const privacyModal = document.getElementById('privacyModal');
const privacyModalContent = privacyModal?.querySelector('.info-modal-content');
const closePrivacyModalButton = document.getElementById('closePrivacyModal');

const openPrivacyModal = () => {
  if (!privacyModal || !privacyModalContent) return;
  privacyModal.classList.add('active');
  privacyModalContent.style.display = 'flex';
  document.body.classList.add('modal-blur');
};

const closePrivacyModal = () => {
  if (!privacyModal || !privacyModalContent) return;
  privacyModal.classList.remove('active');
  privacyModalContent.style.display = 'none';
  document.body.classList.remove('modal-blur');
  if (window.location.pathname === '/privacy') {
    history.replaceState(null, '', '/');
  }
};

if (closePrivacyModalButton) {
  closePrivacyModalButton.addEventListener('click', closePrivacyModal);
}

if (privacyModal) {
  privacyModal.addEventListener('click', (event) => {
    if (event.target === privacyModal) {
      closePrivacyModal();
    }
  });
}

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && privacyModal?.classList.contains('active')) {
    closePrivacyModal();
  }
});

const startStreamCountdown = (element, stream) => {
  if (!element) return;

  const permission = stream?.feeding_permission;
  const feedAllowed = permission === 'ANYONE'
    || permission === 'AUTH_USERS'
    || (permission === 'ADMINS' && stream?.user_is_admin === true);
  if (permission && !feedAllowed) {
    element.textContent = FEEDING_DISABLED_MESSAGE;
    element.classList.remove('stream-countdown--available');
    return;
  }

  const secondsLeft = Math.floor(Number(stream.seconds_left_to_feed));

  if (Number.isFinite(secondsLeft) && secondsLeft >= 0) {
    const endTime = Date.now() + secondsLeft * 1000;
    let lastDisplayed = -1;

    let intervalId;
    const updateCountdown = () => {
      const remainingSeconds = Math.ceil((endTime - Date.now()) / 1000);
      if (remainingSeconds <= 0) {
        element.textContent = 'feeding available';
        element.classList.add('stream-countdown--available');
        clearInterval(intervalId);
        countdownIntervals.delete(intervalId);
        return;
      }
      if (remainingSeconds !== lastDisplayed) {
        lastDisplayed = remainingSeconds;
        element.textContent = `Feeding available in ${formatDuration(remainingSeconds)}`;
        element.classList.remove('stream-countdown--available');
      }
    };

    updateCountdown();
    intervalId = setInterval(updateCountdown, 250);
    countdownIntervals.add(intervalId);
    return;
  }

  const lastFeedDate = parseUTCDate(stream.last_feed_time);
  const cooldownSeconds = Number(stream.feeder_cooldown_time) || 0;

  if (!lastFeedDate || cooldownSeconds <= 0) {
    element.textContent = 'feeding available';
    element.classList.add('stream-countdown--available');
    return;
  }

  let intervalId;
  const updateFallbackCountdown = () => {
    const now = new Date();
    const elapsedSeconds = Math.floor((now.getTime() - lastFeedDate.getTime()) / 1000);
    const remaining = cooldownSeconds - elapsedSeconds;

    if (remaining <= 0) {
      element.textContent = 'feeding available';
      element.classList.add('stream-countdown--available');
      clearInterval(intervalId);
      countdownIntervals.delete(intervalId);
    } else {
      element.textContent = `Feeding available in ${formatDuration(remaining)}`;
      element.classList.remove('stream-countdown--available');
    }
  };

  updateFallbackCountdown();
  intervalId = setInterval(updateFallbackCountdown, 1000);
  countdownIntervals.add(intervalId);
};

function setupStreamPreview(imageElement, snapshotUrl, spinnerElement, fallbackUrl, previousImageUrl = null, streamName = null) {
  if (!imageElement) return null;

  const hideSpinner = () => {
    if (spinnerElement) {
      spinnerElement.classList.add('is-hidden');
    }
  };

  const showSpinner = () => {
    if (spinnerElement) {
      spinnerElement.classList.remove('is-hidden');
    }
  };

  const showFallback = () => {
    if (fallbackUrl) {
      imageElement.src = fallbackUrl;
      imageElement.classList.add('stream-thumb--fallback');
    } else {
      imageElement.src = PREVIEW_FALLBACK_POSTER;
      imageElement.classList.add('stream-thumb--fallback');
    }
    hideSpinner();
  };

  let cleanedUp = false;
  let refreshIntervalId = null;

  // Use snapshot URL if available, otherwise fallback
  const baseImageUrl = snapshotUrl || fallbackUrl;
  
  // If we have a previous image, show it immediately (hide spinner)
  if (previousImageUrl) {
    imageElement.src = previousImageUrl;
    imageElement.classList.remove('stream-thumb--fallback');
    hideSpinner();
  }
  
  if (!baseImageUrl) {
    if (!previousImageUrl) {
      showFallback();
    }
    return () => {
      cleanedUp = true;
      hideSpinner();
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
        snapshotRefreshIntervals.delete(refreshIntervalId);
      }
    };
  }

  let currentBlobUrl = null;

  const loadImage = (useCacheBust = true) => {
    if (cleanedUp) return;

    const isSnapshot = !!snapshotUrl;
    let imageUrl = baseImageUrl;

    if (isSnapshot && useCacheBust) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      imageUrl = `${baseImageUrl}${separator}_t=${Date.now()}`;
    }

    fetch(imageUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        if (cleanedUp) return;
        if (currentBlobUrl) {
          URL.revokeObjectURL(currentBlobUrl);
        }
        currentBlobUrl = URL.createObjectURL(blob);
        imageElement.src = currentBlobUrl;
        imageElement.classList.remove('stream-thumb--fallback');
        hideSpinner();
        if (streamName && !baseImageUrl.includes(PREVIEW_FALLBACK_POSTER)) {
          lastPreviewImages.set(streamName, baseImageUrl);
        }
      })
      .catch(() => {
        if (cleanedUp) return;
        console.warn('Failed to load snapshot:', imageUrl);
        if (!useCacheBust || !snapshotUrl) {
          if (!previousImageUrl) {
            showFallback();
          }
        }
      });
  };

  // Initial load - only show spinner if we don't have a previous image
  if (!previousImageUrl) {
    showSpinner();
  }
  loadImage(true);

  // Set up periodic refresh for snapshot URLs (every 5 seconds)
  if (snapshotUrl) {
    refreshIntervalId = setInterval(() => {
      if (!cleanedUp && imageElement.parentElement) {
        loadImage(true);
      }
    }, SNAPSHOT_REFRESH_FREQUENCY);
    snapshotRefreshIntervals.add(refreshIntervalId);
  }

  return () => {
    cleanedUp = true;
    hideSpinner();
    if (currentBlobUrl) {
      URL.revokeObjectURL(currentBlobUrl);
      currentBlobUrl = null;
    }
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
      snapshotRefreshIntervals.delete(refreshIntervalId);
    }
  };
}

// View elements
const landingView = document.getElementById('landing-view');
const livestreamView = document.getElementById('livestream-view');
const loadingView = document.getElementById('loading-view');
const galleryView = document.getElementById('gallery-view');
const privacyView = document.getElementById('privacy-view');
const landingTabsEl = document.getElementById('landing-tabs');

// Show/hide views
let currentViewName = null;
function showView(viewName) {
  const prevViewName = currentViewName;
  currentViewName = viewName;

  if (prevViewName === 'gallery' && viewName !== 'gallery') {
    resetGalleryFilters();
  }

  landingView.style.display = viewName === 'landing' ? 'block' : 'none';
  livestreamView.style.display = viewName === 'livestream' ? 'block' : 'none';
  loadingView.style.display = viewName === 'loading' ? 'block' : 'none';
  galleryView.style.display = viewName === 'gallery' ? 'block' : 'none';
  if (privacyView) privacyView.style.display = viewName === 'privacy' ? 'block' : 'none';

  if (landingTabsEl) {
    landingTabsEl.style.display = viewName === 'landing' ? 'flex' : 'none';
  }

  if (viewName !== 'landing') {
    hideBannerWhenLeavingLanding();
    clearSnapshotRefreshTimers();
  }

  if (viewName !== 'gallery') {
    cleanupGalleryScroll();
  }

  // Manage body classes for different view states
  document.body.classList.toggle('landing-active', viewName === 'landing');
  document.body.classList.toggle('livestream-active', viewName === 'livestream');
  document.body.classList.toggle('gallery-active', viewName === 'gallery');
  if (viewName !== 'landing') document.body.classList.remove('rankings-tab-active');

  if (viewName === 'livestream') {
    // Nudge scroll to trigger Safari iOS toolbar collapse.
    // All livestream content is position:fixed so this is invisible.
    window.scrollTo(0, 0);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 1, behavior: 'smooth' });
    });
  } else {
    window.scrollTo(0, 0);
  }
}

let streams_list = null;
/** Whether the last /rooms fetch included an auth token (null = not fetched yet). */
let lastRoomsFetchHadAuth = null;

function landingRoomsVisible() {
  return landingView && landingView.style.display !== 'none';
}

function roomsListNeedsFullReload(latestRooms) {
  const prev = streams_list?.rooms;
  if (!prev) return true;
  if (prev.length !== latestRooms.length) return true;
  const prevNames = new Set(prev.map((r) => r.name));
  if (latestRooms.some((r) => !prevNames.has(r.name))) return true;
  const prevHadAdminExtras = prev.some((r) => r.visible === false || r.user_is_admin);
  const latestHasAdminExtras = latestRooms.some((r) => r.visible === false || r.user_is_admin);
  return prevHadAdminExtras !== latestHasAdminExtras;
}

onAuthChange(async () => {
  if (!landingRoomsVisible() || lastRoomsFetchHadAuth === null) return;
  const hasAuth = !!(await getToken());
  if (hasAuth === lastRoomsFetchHadAuth) return;
  loadLivestreams();
});

// Landing page tab functionality
let landingTabsInitialized = false;
let faqInitialized = false;
function initializeLandingTabs() {
  if (landingTabsInitialized) return;
  landingTabsInitialized = true;

  const landingTabs = document.querySelectorAll('.landing-tab');
  const landingTabContents = document.querySelectorAll('.landing-tab-content');

  function switchLandingTab(targetId) {
    landingTabs.forEach(tab => tab.classList.remove('active'));
    landingTabContents.forEach(content => content.classList.remove('active'));

    const targetTab = document.querySelector(`.landing-tab[data-target="${targetId}"]`);
    const targetContent = document.getElementById(targetId);

    if (targetTab && targetContent) {
      targetTab.classList.add('active');
      targetContent.classList.add('active');
    }

    document.body.classList.toggle('rankings-tab-active', targetId === 'leaderboardTabContent');

    if (targetId === 'leaderboardTabContent') {
      loadGlobalLeaderboard(1, { reset: true });
    }
  }

  landingTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const targetId = tab.dataset.target;
      switchLandingTab(targetId);
    });
  });

  initGlobalLeaderboardInfiniteScroll();
  initGlobalLeaderboardFilters();
  initializeFaqAccordion();

  onAuthChange(() => {
    const loginNotice = document.getElementById('globalLeaderboardLoginNotice');
    if (loginNotice) loginNotice.style.display = isLoggedIn() ? 'none' : '';
  });

  const signInFromLeaderboard = document.getElementById('globalLeaderboardSignInLink');
  if (signInFromLeaderboard) {
    signInFromLeaderboard.addEventListener('click', () => openAuthModal());
  }
}

let globalLeaderboardPage = 0;
let globalLeaderboardTotalPages = 0;
let globalLeaderboardIsLoading = false;
let globalLeaderboardPrevCount = null;
let globalLeaderboardPrevRank = 0;
let globalLeaderboardObserver = null;
let globalLeaderboardPeriod = 'all_time';

async function loadGlobalLeaderboard(page, { reset = false } = {}) {
  const list = document.getElementById('globalLeaderboardList');
  const loading = document.getElementById('globalLeaderboardLoading');
  const empty = document.getElementById('globalLeaderboardEmpty');
  const loadMore = document.getElementById('globalLeaderboardLoadMore');
  const loginNotice = document.getElementById('globalLeaderboardLoginNotice');
  if (!list) return;
  if (globalLeaderboardIsLoading) return;

  globalLeaderboardIsLoading = true;

  if (loginNotice) loginNotice.style.display = isLoggedIn() ? 'none' : '';

  if (reset) {
    globalLeaderboardPage = 0;
    globalLeaderboardTotalPages = 0;
    globalLeaderboardPrevCount = null;
    globalLeaderboardPrevRank = 0;
    list.innerHTML = '';
    list.style.display = 'none';
    if (empty) empty.style.display = 'none';
    if (loadMore) loadMore.style.display = 'none';
    if (loading) loading.style.display = 'flex';
    unobserveGlobalLeaderboardSentinel();
    const scrollContainer = list.closest('.global-leaderboard');
    if (scrollContainer) scrollContainer.scrollTop = 0;
  } else if (loadMore) {
    loadMore.style.display = 'flex';
  }

  try {
    const res = await fetch(`${API_URL}/users/top-feeders?page=${page}&per_page=10&period=${encodeURIComponent(globalLeaderboardPeriod)}`);
    if (!res.ok) throw new Error();
    const data = await res.json();
    const users = data.users || [];
    globalLeaderboardTotalPages = data.pages || 1;
    globalLeaderboardPage = data.page || page;

    if (loading) loading.style.display = 'none';
    if (loadMore) loadMore.style.display = 'none';

    if (users.length === 0 && globalLeaderboardPage === 1) {
      if (empty) empty.style.display = '';
      return;
    }

    const self = getBackendUser();

    users.forEach((user) => {
      const row = document.createElement('div');
      row.className = 'leaderboard-row';

      const rank = document.createElement('span');
      rank.className = 'leaderboard-rank';
      const periodCount = user.times_fed_in_period ?? user.times_fed;
      const displayRank = (periodCount === globalLeaderboardPrevCount)
        ? globalLeaderboardPrevRank
        : globalLeaderboardPrevRank + 1;
      globalLeaderboardPrevCount = periodCount;
      globalLeaderboardPrevRank = displayRank;
      rank.textContent = `${displayRank}`;

      const avatar = document.createElement('div');
      avatar.className = 'leaderboard-avatar';
      if (user.avatar_url) {
        const img = document.createElement('img');
        img.src = user.avatar_url;
        img.alt = '';
        img.className = 'leaderboard-avatar-img';
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'leaderboard-avatar-placeholder';
        placeholder.style.display = 'none';
        img.onerror = () => { img.style.display = 'none'; placeholder.style.display = ''; };
        avatar.append(img, placeholder);
      } else {
        const placeholder = document.createElement('img');
        placeholder.src = '/icons/user.svg';
        placeholder.alt = '';
        placeholder.className = 'leaderboard-avatar-placeholder';
        avatar.appendChild(placeholder);
      }

      const isMe = self && self.id === user.id;
      if (isMe) row.classList.add('leaderboard-row--me');

      const name = document.createElement('span');
      name.className = 'leaderboard-name';
      name.textContent = user.display_name || 'user';
      name.addEventListener('click', () => {
        if (isMe) openProfileModal();
        else openUserCard(user.id);
      });

      const count = document.createElement('span');
      count.className = 'leaderboard-count';
      const n = user.times_fed_in_period ?? user.times_fed;
      count.textContent = `${n} feed${n !== 1 ? 's' : ''}`;

      row.append(rank, avatar, name, count);
      list.appendChild(row);
    });

    list.style.display = '';

    if (globalLeaderboardPage < globalLeaderboardTotalPages) {
      observeGlobalLeaderboardSentinel();
    } else {
      unobserveGlobalLeaderboardSentinel();
    }
  } catch {
    if (loading) loading.style.display = 'none';
    if (loadMore) loadMore.style.display = 'none';
    if (list.children.length === 0 && empty) {
      empty.textContent = 'failed to load leaderboard';
      empty.style.display = '';
    }
  } finally {
    globalLeaderboardIsLoading = false;
  }
}

function initGlobalLeaderboardInfiniteScroll() {
  if (globalLeaderboardObserver) return;
  if (typeof IntersectionObserver === 'undefined') return;
  globalLeaderboardObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (globalLeaderboardIsLoading) continue;
      if (globalLeaderboardPage <= 0) continue;
      if (globalLeaderboardPage >= globalLeaderboardTotalPages) continue;
      loadGlobalLeaderboard(globalLeaderboardPage + 1);
    }
  }, { rootMargin: '200px 0px' });
}

function initGlobalLeaderboardFilters() {
  const bar = document.getElementById('globalLeaderboardFilterBar');
  if (!bar) return;
  bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.global-leaderboard-period-btn');
    if (!btn) return;
    const period = btn.dataset.period;
    if (!period || period === globalLeaderboardPeriod) return;
    globalLeaderboardPeriod = period;
    bar.querySelectorAll('.global-leaderboard-period-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    loadGlobalLeaderboard(1, { reset: true });
  });
}

function observeGlobalLeaderboardSentinel() {
  const sentinel = document.getElementById('globalLeaderboardSentinel');
  if (sentinel && globalLeaderboardObserver) {
    globalLeaderboardObserver.observe(sentinel);
  }
}

function unobserveGlobalLeaderboardSentinel() {
  const sentinel = document.getElementById('globalLeaderboardSentinel');
  if (sentinel && globalLeaderboardObserver) {
    globalLeaderboardObserver.unobserve(sentinel);
  }
}

function initializeFaqAccordion() {
  if (faqInitialized) return;
  faqInitialized = true;

  const faqItems = document.querySelectorAll('.faq-item');
  faqItems.forEach((item) => {
    const button = item.querySelector('.faq-question');
    const answer = item.querySelector('.faq-answer');
    if (!button || !answer) return;

    // Ensure answers start hidden
    answer.style.display = 'none';
    button.setAttribute('aria-expanded', 'false');

    const toggle = () => {
      const isOpen = item.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(isOpen));
      answer.style.display = isOpen ? 'block' : 'none';
    };

    // Click on the question
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });

    // Click on the answer area
    answer.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });

    // Click anywhere in the faq item
    item.addEventListener('click', (e) => {
      // Avoid double-toggle when clicking the button (already handled)
      if (e.target === button || button.contains(e.target)) return;
      toggle();
    });
  });
}

// Pull-to-refresh for landing page
const PTR_THRESHOLD = 60;
const PTR_MAX_PULL = 100;
let ptrStartY = 0;
let ptrActive = false;
let ptrRefreshing = false;

function initPullToRefresh() {
  const scrollContainer = landingView;
  const indicator = document.getElementById('ptr-indicator');
  const icon = indicator?.querySelector('.ptr-icon');
  if (!scrollContainer || !indicator || !icon) return;

  const isAtTop = () => window.scrollY <= 0;
  const roomsTab = document.getElementById('roomsTabContent');
  const isRoomsTabActive = () => roomsTab?.classList.contains('active');

  scrollContainer.addEventListener('touchstart', (e) => {
    if (ptrRefreshing || !isAtTop() || !isRoomsTabActive()) return;
    ptrStartY = e.touches[0].clientY;
    ptrActive = true;
  }, { passive: true });

  scrollContainer.addEventListener('touchmove', (e) => {
    if (!ptrActive || ptrRefreshing) return;
    const currentY = e.touches[0].clientY;
    const pullDistance = currentY - ptrStartY;

    if (!isAtTop() && pullDistance <= 0) {
      ptrActive = false;
      indicator.style.height = '';
      indicator.style.opacity = '';
      indicator.classList.remove('ptr-pulling');
      return;
    }

    if (isAtTop() && pullDistance > 0) {
      e.preventDefault();
    }

    const clampedPull = Math.max(0, pullDistance);
    const dampened = Math.min(PTR_MAX_PULL, clampedPull * 0.5);
    const progress = Math.min(1, dampened / PTR_THRESHOLD);

    indicator.classList.add('ptr-pulling');
    indicator.style.height = `${dampened}px`;
    indicator.style.opacity = String(progress);
    icon.style.transform = `rotate(${-progress * 180}deg)`;
  }, { passive: false });

  scrollContainer.addEventListener('touchend', async () => {
    if (!ptrActive || ptrRefreshing) {
      ptrActive = false;
      return;
    }
    ptrActive = false;

    const currentHeight = parseFloat(indicator.style.height) || 0;
    const reachedThreshold = currentHeight >= PTR_THRESHOLD;

    indicator.classList.remove('ptr-pulling');

    if (!reachedThreshold) {
      indicator.style.height = '';
      indicator.style.opacity = '';
      icon.style.transform = '';
      return;
    }

    ptrRefreshing = true;
    indicator.classList.add('ptr-refreshing');
    indicator.style.height = '';
    indicator.style.opacity = '';
    icon.style.transform = '';

    try {
      await loadLivestreams();
    } finally {
      ptrRefreshing = false;
      indicator.classList.remove('ptr-refreshing');
    }
  }, { passive: true });
}

// Landing page functionality
async function loadLivestreams() {
  window.visitedLanding = true;
  stopLandingDataRefresh();

  // Immediate UI update for instant navigation
  document.body.classList.remove('livestream-active');
  const livestreamRoot = document.getElementById('livestream-view');
  if (livestreamRoot) {
    livestreamRoot.classList.remove('is-active');
  }
  showView('landing');
  window.scrollTo(0, 0);
  refreshBannerLayout();
  initializeLandingTabs();
  requestAnimationFrame(() => {
    if (landingTabsEl) landingTabsEl.style.display = 'flex';
  });

  const list = document.getElementById("stream-list");
  if (!list.querySelector('.stream-card:not(.stream-card--skeleton)')) {
    list.innerHTML = Array.from({ length: 4 }, (_, i) => `
      <article class="stream-card stream-card--skeleton" aria-hidden="true">
        <div class="stream-thumb-wrapper">
          <div class="skeleton-shimmer stream-skeleton-thumb"></div>
        </div>
        <div class="stream-card-body">
          <div class="skeleton-shimmer stream-skeleton-title"${i % 2 ? ' style="width:60%"' : ''}></div>
          <div class="skeleton-shimmer stream-skeleton-subtitle"></div>
        </div>
      </article>
    `).join('');
  }

  try {
    //showView('loading');
    await waitForAuth();
    const token = await getToken();
    lastRoomsFetchHadAuth = !!token;
    const res = await fetch(API_URL + '/rooms', token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
    streams_list = await res.json();

    // Show maintenance banner when no rooms are available
    const maintenanceBanner = document.getElementById('maintenance-banner');
    if (!streams_list.rooms || streams_list.rooms.length === 0) {
      if (maintenanceBanner) maintenanceBanner.style.display = 'flex';
      list.innerHTML = '';
      clearCountdownTimers();
      clearSnapshotRefreshTimers();
      landingCardMap.clear();
      showView('landing');
      return;
    }
    if (maintenanceBanner) maintenanceBanner.style.display = 'none';
    
    // Save current preview images before clearing
    const existingCards = list.querySelectorAll('.stream-card');
    existingCards.forEach(card => {
      const img = card.querySelector('.stream-thumb');
      const streamName = card.dataset.streamName;
      if (img && streamName && img.src && !img.src.startsWith('blob:') && !img.src.includes(PREVIEW_FALLBACK_POSTER)) {
        // Only save if it's a real image (not fallback)
        // Remove cache-busting parameters if present
        let imageUrl = img.src;
        const cacheBustIndex1 = imageUrl.indexOf('?_t=');
        const cacheBustIndex2 = imageUrl.indexOf('&_t=');
        const cacheBustIndex = cacheBustIndex1 !== -1 ? cacheBustIndex1 : (cacheBustIndex2 !== -1 ? cacheBustIndex2 : -1);
        if (cacheBustIndex !== -1) {
          imageUrl = imageUrl.substring(0, cacheBustIndex);
        }
        lastPreviewImages.set(streamName, imageUrl);
      }
    });
    
    list.innerHTML = ""; // clear existing
    clearCountdownTimers();
    clearSnapshotRefreshTimers();
    landingCardMap.clear();

    // Create all cards first without loading previews
    const streamCards = streams_list.rooms.map(stream => {
      const title = escapeHTML(stream.display_name);
      const roomNameLabel = escapeHTML(`/${stream.name}`);
      const card = document.createElement("article");
      card.className = stream.visible === false ? "stream-card stream-card--hidden" : "stream-card";
      card.setAttribute("tabindex", "0");
      card.ariaLabel = `Open ${stream.display_name} livestream`;

      const rawDescription = stream.description && stream.description.trim()
        ? stream.description.trim()
        : "No description available";
      const description = escapeHTML(rawDescription);
      const viewerCountRaw = Number.isFinite(stream.num_viewers) ? stream.num_viewers : 0;
      const viewerCount = viewerCountRaw < 0 ? 0 : viewerCountRaw;
      const viewerCountFormatted = viewerCount.toLocaleString();
      const viewerLabel = viewerCount === 1 ? "viewer" : "viewers";
      const viewerDisplay = `${viewerCountFormatted} ${viewerLabel}`;

      const adminBadgeHTML = stream.user_is_admin ? `
            <span class="stream-badge stream-badge--status" title="Room admin" aria-label="Room admin">
              <img class="stream-badge-icon" src="/icons/crown.svg" alt="" aria-hidden="true">
            </span>` : '';
      const hiddenBadgeHTML = stream.visible === false ? `
            <span class="stream-badge stream-badge--status" title="Room hidden" aria-label="Room hidden">
              <img class="stream-badge-icon" src="/icons/lock.svg" alt="" aria-hidden="true">
            </span>` : '';
      const brokenBadgeHTML = stream.stream_working === false ? `
            <span class="stream-badge stream-badge--status" title="Stream not working" aria-label="Stream not working">
              <img class="stream-badge-icon" src="/icons/link-broken.svg" alt="" aria-hidden="true">
            </span>` : '';

      card.innerHTML = `
        <div class="stream-thumb-wrapper">
          <div class="stream-thumb-spinner">
            <div class="spinner spinner--thumb"></div>
          </div>
          <img class="stream-thumb">
          <div class="stream-badges">
            <span class="stream-badge stream-badge--viewers" title="${viewerDisplay}" aria-label="${viewerDisplay}">
              <img class="stream-badge-icon" src="/icons/user.svg" alt="" aria-hidden="true">
              <span class="stream-badge-count">${viewerCountFormatted}</span>
            </span>${adminBadgeHTML}${hiddenBadgeHTML}${brokenBadgeHTML}
          </div>
        </div>
        <div class="stream-card-body">
          <div class="stream-card-heading">
            <h2 class="stream-title">${title}</h2> <p class="stream-room-name">${roomNameLabel}</p>
          </div>
          <p class="stream-countdown"></p>
        </div>
      `;

      const slug = generateSlug(stream.name);
      const previewElement = card.querySelector(".stream-thumb");
      const spinnerElement = card.querySelector(".stream-thumb-spinner");
      const countdownElement = card.querySelector(".stream-countdown");
      const badgeElement = card.querySelector(".stream-badge--viewers");
      const badgeCountElement = card.querySelector(".stream-badge-count");
      
      // Store stream name on card for later reference
      card.dataset.streamName = stream.name;
      
      if (countdownElement) {
        startStreamCountdown(countdownElement, stream);
      }

      const navigateToStream = () => {
        clearCountdownTimers();
        router.navigate(`/${slug}`, stream);
      };

      card.addEventListener("click", navigateToStream);
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigateToStream();
        }
      });

      list.appendChild(card);
      landingCardMap.set(stream.name, {
        cardElement: card,
        badgeElement,
        badgeCountElement,
        countdownElement
      });

      return {
        stream,
        card,
        previewElement,
        spinnerElement,
        cleanupPreview: null
      };
    });

    // Load preview images using snapshot URLs
    streamCards.forEach((streamCard) => {
      const { stream, previewElement, spinnerElement } = streamCard;
      if (previewElement && previewElement.parentElement) {
        if (stream.stream_working === false) {
          previewElement.src = PREVIEW_FALLBACK_POSTER;
          previewElement.classList.add('stream-thumb--fallback');
          if (spinnerElement) spinnerElement.classList.add('is-hidden');
          streamCard.cleanupPreview = null;
        } else {
          const previousImage = lastPreviewImages.get(stream.name);
          streamCard.cleanupPreview = setupStreamPreview(
            previewElement,
            stream.snapshot_url,
            spinnerElement,
            stream.preview_image_url || stream.thumbnail_url,
            previousImage,
            stream.name
          );
        }
      }
    });

    const comingSoonCard = document.createElement('article');
    comingSoonCard.className = 'stream-card stream-card--coming-soon';
    comingSoonCard.setAttribute('tabindex', '0');
    comingSoonCard.setAttribute('role', 'button');
    comingSoonCard.setAttribute('aria-label', 'Learn how to get your cat featured');
    comingSoonCard.innerHTML = `
      <div class="stream-card-body">
        <p class="coming-soon-label">more coming soon...</p>
        <p class="coming-soon-subtext">know a cat that could be featured in this page?<br><span class="coming-soon-link">contact us</span></p>
      </div>
    `;
    const handleComingSoonActivate = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      openComingSoonModal();
    };
    comingSoonCard.addEventListener('click', handleComingSoonActivate);
    comingSoonCard.addEventListener('keydown', handleComingSoonActivate);
    list.appendChild(comingSoonCard);

    startLandingDataRefresh();
  } catch (err) {
    console.error("Error loading livestreams:", err);
    document.getElementById("stream-list").innerHTML = "<p>Failed to load streams.</p>";
    showView('landing');
  }
}

async function refreshLandingStreamData() {
  if (landingRefreshInFlight) {
    return;
  }

  const landingVisible = landingView && landingView.style.display !== 'none';
  if (!landingVisible) {
    return;
  }

  landingRefreshInFlight = true;
  try {
    const token = await getToken();
    const res = await fetch(API_URL + '/rooms', token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
    const latestData = await res.json();
    if (!latestData || !Array.isArray(latestData.rooms)) {
      return;
    }

    const maintenanceBanner = document.getElementById('maintenance-banner');
    if (latestData.rooms.length === 0) {
      if (maintenanceBanner) maintenanceBanner.style.display = 'flex';
      return;
    }

    if (maintenanceBanner) maintenanceBanner.style.display = 'none';

    if (landingCardMap.size === 0 || roomsListNeedsFullReload(latestData.rooms)) {
      loadLivestreams();
      return;
    }

    streams_list = latestData;
    lastRoomsFetchHadAuth = !!token;
    clearCountdownTimers();

    const list = document.getElementById('stream-list');

    latestData.rooms.forEach((stream) => {
      const cardRefs = landingCardMap.get(stream.name);
      if (!cardRefs) {
        return;
      }

      const viewerCountRaw = Number.isFinite(stream.num_viewers) ? stream.num_viewers : 0;
      const viewerCount = viewerCountRaw < 0 ? 0 : viewerCountRaw;
      const viewerCountFormatted = viewerCount.toLocaleString();
      const viewerLabel = viewerCount === 1 ? 'viewer' : 'viewers';
      const viewerDisplay = `${viewerCountFormatted} ${viewerLabel}`;

      if (cardRefs.badgeElement) {
        cardRefs.badgeElement.title = viewerDisplay;
        cardRefs.badgeElement.setAttribute('aria-label', viewerDisplay);
      }

      if (cardRefs.badgeCountElement) {
        cardRefs.badgeCountElement.textContent = viewerCountFormatted;
      }

      if (cardRefs.countdownElement) {
        startStreamCountdown(cardRefs.countdownElement, stream);
      }
    });

    // Reorder cards to match the API response order
    if (list) {
      const comingSoonCard = list.querySelector('.stream-card--coming-soon');
      latestData.rooms.forEach((stream) => {
        const cardRefs = landingCardMap.get(stream.name);
        if (cardRefs?.cardElement) {
          list.appendChild(cardRefs.cardElement);
        }
      });
      if (comingSoonCard) {
        list.appendChild(comingSoonCard);
      }
    }
  } catch (err) {
    console.warn('Error refreshing landing streams:', err);
  } finally {
    landingRefreshInFlight = false;
  }
}

// Archived rooms load on demand into their own grid, which the landing refresh never touches.
const archivedRoomsToggle = document.getElementById('archivedRoomsToggle');
const archivedRoomsList = document.getElementById('archived-stream-list');
let archivedRooms = [];

function createArchivedRoomCard(stream) {
  const displayName = stream.display_name || stream.name;
  const card = document.createElement('article');
  card.className = 'stream-card';
  card.setAttribute('tabindex', '0');
  card.ariaLabel = `Open ${displayName} archived room`;
  card.innerHTML = `
    <div class="stream-thumb-wrapper">
      <div class="stream-thumb-spinner">
        <div class="spinner spinner--thumb"></div>
      </div>
      <img class="stream-thumb">
    </div>
    <div class="stream-card-body">
      <div class="stream-card-heading">
        <h2 class="stream-title">${escapeHTML(displayName)}</h2> <p class="stream-room-name">${escapeHTML(`/${stream.name}`)}</p>
      </div>
      <p class="stream-countdown">archived</p>
    </div>
  `;

  // Nothing updates the snapshot of an archived room, so it isn't polled like a live one
  const preview = card.querySelector('.stream-thumb');
  const spinner = card.querySelector('.stream-thumb-spinner');
  preview.addEventListener('load', () => spinner.classList.add('is-hidden'));
  preview.addEventListener('error', () => {
    preview.src = PREVIEW_FALLBACK_POSTER;
    preview.classList.add('stream-thumb--fallback');
  }, { once: true });
  preview.src = stream.snapshot_url || PREVIEW_FALLBACK_POSTER;

  const navigateToRoom = () => {
    clearCountdownTimers();
    router.navigate(`/${generateSlug(stream.name)}`, stream);
  };

  card.addEventListener('click', navigateToRoom);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateToRoom();
    }
  });

  return card;
}

async function showArchivedRooms() {
  archivedRoomsToggle.disabled = true;
  try {
    await waitForAuth();
    const token = await getToken();
    const res = await fetch(API_URL + '/rooms?archived=true', token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
    const { rooms } = await res.json();
    if (rooms.length > 0) {
      archivedRoomsList.replaceChildren(...rooms.map(createArchivedRoomCard));
    } else {
      archivedRoomsList.innerHTML = '<p class="archived-rooms-message">No archived rooms yet.</p>';
    }
    archivedRooms = rooms;
  } catch (err) {
    console.error('Error loading archived rooms:', err);
    archivedRoomsList.innerHTML = '<p class="archived-rooms-message">Failed to load archived rooms.</p>';
  }
  archivedRoomsList.style.display = '';
  archivedRoomsToggle.textContent = '[hide archived rooms]';
  archivedRoomsToggle.setAttribute('aria-expanded', 'true');
  archivedRoomsToggle.disabled = false;
}

function hideArchivedRooms() {
  archivedRoomsList.style.display = 'none';
  archivedRoomsList.innerHTML = '';
  archivedRoomsToggle.textContent = '[show archived rooms]';
  archivedRoomsToggle.setAttribute('aria-expanded', 'false');
}

archivedRoomsToggle.addEventListener('click', () => {
  if (archivedRoomsToggle.getAttribute('aria-expanded') === 'true') {
    hideArchivedRooms();
  } else {
    showArchivedRooms();
  }
});

// Generate URL-friendly slug from stream name
function generateSlug(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
}

// Livestream functionality
async function loadLivestream() {
  stopLandingDataRefresh();
  const params = router.getParams();
  const slug = params.slug;
  
  if (!slug) {
    console.error('No slug provided');
    router.navigate('/');
    return;
  }

  try {
    showView('livestream'); // FIXME make the loading view prettier

    if (streams_list) {
      const stream = streams_list.rooms.find(s => s.name === slug);
      if (stream) {
        await initializeLivestream(stream);
        return;
      }
    }
    // Archived rooms aren't in streams_list, but the ones shown on the landing are already loaded
    const archivedRoom = archivedRooms.find(s => s.name === slug);
    if (archivedRoom) {
      await initializeLivestream(archivedRoom);
      return;
    }
    // If we don't have the streams list, or we could not find the desired stream in the list, fetch the stream from the api.
    showRoomLoadingState();
    await waitForAuth();
    const token = await getToken();
    const res = await fetch(API_URL + '/rooms/' + slug, token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
    const stream = await res.json();
    await initializeLivestream(stream);
    return;
    
  } catch (err) {
    console.error("Error loading livestream:", err);
    router.navigate('/'); // Go to the home page, which loads the list of livestreams
  }
}

// Initialize livestream functionality
async function initializeLivestream(stream) {
  await initializeLivestreamComponents(stream);
}

// Click particle effect
function explodeOnClick(e) {
  let toPopulate = document.createElement("div");
  toPopulate.classList.add("explode");

  toPopulate.style.top = e.clientY + "px";
  toPopulate.style.left = e.clientX + "px";

  document.querySelector("body").appendChild(toPopulate);

  setTimeout(() => toPopulate.remove(), 1200);

  for (let i = 0; i < 2; i++) {
    setTimeout(() => {
      let newStar = document.createElement("p");
      let j = 0;
      let xDirection = Math.random() < 0.5 ? -1 : 1;
      let xDistance = Math.random() * 100;
      const chars = ['₊', '˚', '⊹'];
      newStar.textContent = chars[Math.floor(Math.random() * chars.length)];
      toPopulate.appendChild(newStar);

      let timerId = setInterval(() => {
        let yTrans = -(-(1 / 40) * (j - 20) ** 2 + 10) + "px";
        let xTrans = xDirection * (xDistance * (j / 100)) + "px";
        newStar.style.transform = `translateX(${xTrans}) translateY(${yTrans})`;

        j += 1;
      }, 25);

      setTimeout(() => {
        clearInterval(timerId);
        newStar.remove();
      }, 5000);
    }, Math.floor(i / 3) * 50);
  }
}

document.addEventListener("click", explodeOnClick);

// Liked sightings – localStorage for guests, server for authenticated users
const LIKED_STORAGE_KEY = 'likedSightings';
const serverLikedIds = new Set();

const getLikedStorage = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LIKED_STORAGE_KEY));
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
    return {};
  } catch { return {}; }
};

const setLikedStorage = (data) => {
  localStorage.setItem(LIKED_STORAGE_KEY, JSON.stringify(data));
};

const getLikedSightings = (room) => {
  const data = getLikedStorage();
  return Array.isArray(data[room]) ? data[room] : [];
};

const setLikedSightings = (room, ids) => {
  const data = getLikedStorage();
  data[room] = ids;
  setLikedStorage(data);
};

const isSightingLiked = (room, id) => {
  if (isLoggedIn()) return serverLikedIds.has(id);
  return getLikedSightings(room).includes(id);
};

const toggleLikeSighting = async (room, id) => {
  if (isLoggedIn()) {
    const wasLiked = serverLikedIds.has(id);
    if (wasLiked) {
      serverLikedIds.delete(id);
      try {
        const res = await authFetch(`${API_URL}/cat_sightings/${id}/like`, { method: 'DELETE' });
        if (!res.ok && res.status !== 404) {
          serverLikedIds.add(id);
          return true;
        }
      } catch {
        serverLikedIds.add(id);
        return true;
      }
      return false;
    } else {
      serverLikedIds.add(id);
      try {
        const res = await authFetch(`${API_URL}/cat_sightings/${id}/like`, { method: 'POST' });
        if (!res.ok && res.status !== 409) {
          serverLikedIds.delete(id);
          return false;
        }
      } catch {
        serverLikedIds.delete(id);
        return false;
      }
      return true;
    }
  }
  const liked = getLikedSightings(room);
  const idx = liked.indexOf(id);
  if (idx === -1) liked.push(id);
  else liked.splice(idx, 1);
  setLikedSightings(room, liked);
  return idx === -1;
};

const fetchUserLikedIds = async () => {
  const user = getBackendUser();
  if (!user?.id) return;
  try {
    let page = 1;
    let pages = 1;
    serverLikedIds.clear();
    while (page <= pages) {
      const res = await fetch(`${API_URL}/users/${user.id}/liked_cat_sightings?page=${page}&per_page=25`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.sightings) data.sightings.forEach(s => serverLikedIds.add(s.id));
      pages = data.pages || 1;
      page++;
    }
  } catch { /* non-critical */ }
};

// Gallery filter/sort state
let gallerySortOrder = 'newest'; // 'newest' | 'oldest'
let galleryLikedOnly = false;

function resetGalleryFilters() {
  gallerySortOrder = 'newest';
  galleryLikedOnly = false;

  const filterBar = document.getElementById('galleryFilterBar');
  if (!filterBar) return;

  filterBar.querySelectorAll('.gallery-sort-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.sort === gallerySortOrder);
  });

  const likedToggle = filterBar.querySelector('.gallery-liked-toggle');
  if (!likedToggle) return;

  likedToggle.classList.toggle('active', galleryLikedOnly);
  const heartIcon = likedToggle.querySelector('.gallery-filter-heart-icon');
  if (heartIcon) heartIcon.src = `/icons/${galleryLikedOnly ? 'heart-full' : 'heart'}.svg`;
}

// Gallery functionality
const galleryGrid = document.getElementById('gallery-grid');
const galleryEmpty = document.getElementById('gallery-empty');
const galleryLoading = document.getElementById('gallery-loading');
const galleryRoomName = document.querySelector('.gallery-room-name');

const updateGalleryStickyTop = () => {
  const header = galleryView?.querySelector('.gallery-header');
  if (header && galleryGrid) {
    const bottom = header.getBoundingClientRect().bottom;
    galleryGrid.style.setProperty('--gallery-sticky-top', `${bottom}px`);
  }
};
const galleryModal = document.getElementById('galleryModal');
const galleryModalImage = document.getElementById('galleryModalImage');
const galleryModalVideo = document.getElementById('galleryModalVideo');
const galleryModalDate = document.getElementById('galleryModalDate');
const galleryModalVideoTimeWrap = document.getElementById('galleryModalVideoTimeWrap');
const galleryModalVideoTime = document.getElementById('galleryModalVideoTime');
const galleryModalClose = document.getElementById('galleryModalClose');
const galleryModalShare = document.getElementById('galleryModalShare');
const galleryModalPrev = document.getElementById('galleryModalPrev');
const galleryModalNext = document.getElementById('galleryModalNext');
const galleryModalLike = document.getElementById('galleryModalLike');
const galleryInfoBtn = document.querySelector('.gallery-info-btn');
const galleryTitle = document.querySelector('.gallery-title');
const galleryPlayPause = document.getElementById('galleryModalPlayPause');
const galleryPlayIcon = document.getElementById('playIcon');
const galleryPauseIcon = document.getElementById('pauseIcon');
const galleryReplayIcon = document.getElementById('replayIcon');
const galleryProgress = document.getElementById('galleryProgress');
const galleryProgressFill = document.getElementById('galleryProgressFill');
const galleryProgressThumb = document.getElementById('galleryProgressThumb');
const galleryModalSpinner = document.getElementById('galleryModalSpinner');
const gallerySpeed = document.getElementById('galleryModalSpeed');
const SPEED_STEPS = [1, 2, 8];
let currentSpeedIndex = 0;
let videoEnded = false;
let isDraggingProgress = false;

let currentSightings = [];
let currentSightingIndex = 0;
let galleryCurrentPage = 1;
let galleryTotalPages = 1;
let galleryTotalSightings = 0;
let galleryCurrentRoom = null;
let galleryIsLoadingMore = false;
let galleryScrollHandler = null;
let galleryPtrStartY = 0;
let galleryPtrActive = false;
let galleryPtrRefreshing = false;

function initGalleryPullToRefresh() {
  const scrollContainer = galleryView;
  const indicator = document.getElementById('gallery-ptr-indicator');
  const icon = indicator?.querySelector('.ptr-icon');
  if (!scrollContainer || !indicator || !icon) return;

  const isAtTop = () => window.scrollY <= 0;

  scrollContainer.addEventListener('touchstart', (e) => {
    if (galleryPtrRefreshing || !isAtTop()) return;
    galleryPtrStartY = e.touches[0].clientY;
    galleryPtrActive = true;
  }, { passive: true });

  scrollContainer.addEventListener('touchmove', (e) => {
    if (!galleryPtrActive || galleryPtrRefreshing) return;
    const currentY = e.touches[0].clientY;
    const pullDistance = currentY - galleryPtrStartY;

    if (!isAtTop() && pullDistance <= 0) {
      galleryPtrActive = false;
      indicator.style.height = '';
      indicator.style.opacity = '';
      indicator.classList.remove('ptr-pulling');
      return;
    }

    if (isAtTop() && pullDistance > 0) {
      e.preventDefault();
    }

    const clampedPull = Math.max(0, pullDistance);
    const dampened = Math.min(PTR_MAX_PULL, clampedPull * 0.5);
    const progress = Math.min(1, dampened / PTR_THRESHOLD);

    indicator.classList.add('ptr-pulling');
    indicator.style.height = `${dampened}px`;
    indicator.style.opacity = String(progress);
    icon.style.transform = `rotate(${-progress * 180}deg)`;
  }, { passive: false });

  scrollContainer.addEventListener('touchend', async () => {
    if (!galleryPtrActive || galleryPtrRefreshing) {
      galleryPtrActive = false;
      return;
    }
    galleryPtrActive = false;

    const currentHeight = parseFloat(indicator.style.height) || 0;
    const reachedThreshold = currentHeight >= PTR_THRESHOLD;

    indicator.classList.remove('ptr-pulling');

    if (!reachedThreshold) {
      indicator.style.height = '';
      indicator.style.opacity = '';
      icon.style.transform = '';
      return;
    }

    galleryPtrRefreshing = true;
    indicator.classList.add('ptr-refreshing');
    indicator.style.height = '';
    indicator.style.opacity = '';
    icon.style.transform = '';

    try {
      await loadGallery();
    } finally {
      galleryPtrRefreshing = false;
      indicator.classList.remove('ptr-refreshing');
    }
  }, { passive: true });
}

const formatSightingDate = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return 'Unknown date';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  let h = date.getHours();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}, ${h}:${min}${ampm}`;
};

const formatGalleryModalDateLine = (createdAt, roomSlug) => {
  const dateStr = createdAt ? formatSightingDate(createdAt).toLowerCase() : '';
  const room = roomSlug ? `/${roomSlug}` : '';
  if (dateStr && room) return `${dateStr} ${room}`;
  return `${dateStr}${room}`;
};

const formatShortDate = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return '';
  const mon = date.toLocaleDateString('en-US', { month: 'short' }).toLowerCase();
  const day = date.getDate();
  let h = date.getHours();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${mon} ${day}, ${h}:${min}${ampm}`;
};

const formatShortTime = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return '';
  let h = date.getHours();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${min}${ampm}`;
};

const getDayKey = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return null;
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatDayLabel = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sightingDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - sightingDay) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  const mon = date.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
  const day = date.getDate();
  const yr = date.getFullYear();
  return yr === now.getFullYear() ? `${mon} ${day}` : `${mon} ${day}, ${yr}`;
};

const createDayGroup = (dateString) => {
  const group = document.createElement('div');
  group.className = 'gallery-day-group';
  group.dataset.dayKey = getDayKey(dateString) || '';

  const sep = document.createElement('div');
  sep.className = 'gallery-day-separator';
  sep.innerHTML = `<span class="gallery-day-label">${escapeHTML(formatDayLabel(dateString))}</span>`;

  const cards = document.createElement('div');
  cards.className = 'gallery-day-cards';

  group.appendChild(sep);
  group.appendChild(cards);
  return { group, cards };
};

let lastRenderedDay = null;
let currentDayCards = null;
let galleryLoadVersion = 0;

const isVideoUrl = (url) => /\.mp4(\?.*)?$/i.test(url);

const updatePlayPauseIcon = () => {
  if (!galleryPlayPause) return;
  const ended = galleryModalVideo.ended;
  const paused = galleryModalVideo.paused;
  if (ended) {
    videoEnded = true;
  } else if (!paused) {
    videoEnded = false;
  }
  galleryPlayIcon.style.display = (!videoEnded && paused) ? 'inline' : 'none';
  galleryPauseIcon.style.display = (!paused && !ended) ? 'inline' : 'none';
  galleryReplayIcon.style.display = videoEnded ? 'inline' : 'none';
};

let modalSpinnerTimeout = null;
let currentModalVideoDuration = null;
const showModalSpinner = () => {
  clearTimeout(modalSpinnerTimeout);
  modalSpinnerTimeout = setTimeout(() => galleryModalSpinner.classList.add('is-visible'), 500);
};
const hideModalSpinner = () => {
  clearTimeout(modalSpinnerTimeout);
  galleryModalSpinner.classList.remove('is-visible');
};

const showModalMedia = (url, thumbnailUrl, apiDuration = null) => {
  const video = isVideoUrl(url);
  const wrapper = galleryModalVideo.parentElement;
  currentModalVideoDuration = Number.isFinite(Number(apiDuration)) ? Number(apiDuration) : null;
  galleryModalImage.style.display = video ? 'none' : 'block';
  galleryModalVideo.style.display = video ? 'block' : 'none';
  if (galleryPlayPause) galleryPlayPause.style.display = video ? 'flex' : 'none';
  if (galleryProgress) {
    galleryProgress.style.display = video ? 'flex' : 'none';
    galleryProgressFill.style.width = '0%';
    galleryProgressThumb.style.left = '0%';
  }
  if (galleryModalVideoTimeWrap) {
    galleryModalVideoTimeWrap.style.display = video ? 'flex' : 'none';
  }
  if (gallerySpeed) {
    gallerySpeed.style.display = video ? 'inline-flex' : 'none';
    currentSpeedIndex = 0;
    gallerySpeed.textContent = '1x';
  }
  showModalSpinner();
  if (video) {
    galleryModalImage.src = '';
    videoEnded = false;
    videoInitialLoad = true;
    galleryModalVideo.currentTime = 0;
    wrapper.classList.add('is-video-loading');
    if (thumbnailUrl) {
      const probe = new Image();
      probe.onload = () => {
        wrapper.style.aspectRatio = `${probe.naturalWidth} / ${probe.naturalHeight}`;
      };
      probe.src = thumbnailUrl;
    }
    galleryModalVideo.src = url;
    galleryModalVideo.playbackRate = 1;
    galleryModalVideo.play().catch(() => {});
    updateGalleryVideoTime();
    updatePlayPauseIcon();
  } else {
    galleryModalVideo.pause();
    galleryModalVideo.removeAttribute('src');
    galleryModalVideo.load();
    wrapper.classList.remove('is-video-loading');
    wrapper.style.aspectRatio = '';
    galleryModalImage.src = url;
  }
};

const updateGalleryUrl = (sighting) => {
  const basePath = `/${galleryCurrentRoom}/gallery`;
  if (sighting && sighting.id) {
    history.replaceState(null, '', `${basePath}/${sighting.id}`);
  } else {
    history.replaceState(null, '', basePath);
    if (window.routeParams) delete window.routeParams.sighting_id;
  }
};

const getSharedSightingId = () => {
  const params = router.getParams();
  return params.sighting_id || null;
};

const showGalleryImage = (index, updateUrl = true) => {
  if (index < 0 || index >= currentSightings.length) return;
  
  currentSightingIndex = index;
  const sighting = currentSightings[index];
  
  showModalMedia(sighting.url, sighting.thumbnail_url, sighting.duration);
  const roomSlug = sighting.room_name || galleryCurrentRoom || '';
  galleryModalDate.textContent = formatGalleryModalDateLine(sighting.created_at, roomSlug);
  
  galleryModalPrev.style.display = index > 0 ? 'flex' : 'none';
  const hasMoreToLoad = galleryCurrentPage < galleryTotalPages;
  const isLastLoaded = index >= currentSightings.length - 1;
  galleryModalNext.style.display = (!isLastLoaded || hasMoreToLoad) ? 'flex' : 'none';
  
  updateModalLikeState();

  if (updateUrl) {
    updateGalleryUrl(sighting);
  }
};

const openGalleryModal = (index, updateUrl = true) => {
  showGalleryImage(index, updateUrl);
  galleryModal.classList.add('active');
  modalOpen();
};

window.openGalleryWithSightings = (sightings, index) => {
  currentSightings = sightings;
  galleryCurrentPage = 1;
  galleryTotalPages = 1;
  openGalleryModal(index, false);
};

let sharedSighting = null;

const closeGalleryModal = () => {
  galleryModal.classList.remove('active');
  modalClose();
  galleryModalImage.src = '';
  galleryModalVideo.pause();
  galleryModalVideo.removeAttribute('src');
  galleryModalVideo.load();
  const w = galleryModalVideo.parentElement;
  w.classList.remove('is-video-loading');
  w.style.aspectRatio = '';
  galleryModalImage.style.display = 'block';
  galleryModalVideo.style.display = 'none';
  if (galleryPlayPause) galleryPlayPause.style.display = 'none';
  if (gallerySpeed) gallerySpeed.style.display = 'none';
  hideModalSpinner();
  sharedSighting = null;
  
  updateGalleryUrl(null);
};

const openSharedImage = async () => {
  const sightingId = getSharedSightingId();
  if (!sightingId) return false;

  try {
    const res = await fetch(`${API_URL}/rooms/${galleryCurrentRoom}/cat_sightings/${sightingId}`);
    if (!res.ok) return false;
    const shared = await res.json();
    if (!shared || !shared.url) return false;

    sharedSighting = shared;
    currentSightingIndex = -1;

    galleryModalVideo.muted = true;
    showModalMedia(shared.url, shared.thumbnail_url, shared.duration);
    const sharedRoom = shared.room_name || galleryCurrentRoom || '';
    galleryModalDate.textContent = formatGalleryModalDateLine(shared.created_at, sharedRoom);

    galleryModalPrev.style.display = 'none';
    galleryModalNext.style.display = 'none';

    galleryModal.classList.add('active');
    modalOpen();
    updateModalLikeState();

    return true;
  } catch (err) {
    console.error('Failed to load shared sighting:', err);
    return false;
  }
};


const navigateGallery = async (direction) => {
  const newIndex = currentSightingIndex + direction;
  
  if (newIndex < 0) return;
  
  if (newIndex < currentSightings.length) {
    showGalleryImage(newIndex, true);
  } else if (galleryCurrentPage < galleryTotalPages && !galleryIsLoadingMore) {
    await loadMoreGallerySightings();
    if (newIndex < currentSightings.length) {
      showGalleryImage(newIndex, true);
    }
  }
};

if (galleryModalClose) {
  galleryModalClose.addEventListener('click', (e) => {
    explodeOnClick(e);
    closeGalleryModal();
  });
}

if (galleryModalShare) {
  galleryModalShare.addEventListener('click', async (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    const sighting = currentSightings[currentSightingIndex];
    if (!sighting) return;
    const shareUrl = `${window.location.origin}/${galleryCurrentRoom}/gallery/${sighting.id}`;

    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        galleryModalShare.classList.add('shared');
        const orig = galleryModalShare.innerHTML;
        galleryModalShare.textContent = '✓';
        setTimeout(() => {
          galleryModalShare.innerHTML = orig;
          galleryModalShare.classList.remove('shared');
        }, 1500);
      } catch (_) {}
    }
  });
}

const updateModalLikeState = () => {
  if (!galleryModalLike || !galleryCurrentRoom) return;
  const sighting = currentSightings[currentSightingIndex] || sharedSighting;
  if (!sighting) return;
  const liked = isSightingLiked(galleryCurrentRoom, sighting.id);
  galleryModalLike.classList.toggle('is-liked', liked);
  const icon = galleryModalLike.querySelector('.gallery-modal-like-icon');
  if (icon) icon.src = `/icons/${liked ? 'heart-full' : 'heart'}.svg`;
};

const syncCardLikeState = (sightingId) => {
  if (!galleryCurrentRoom) return;
  const liked = isSightingLiked(galleryCurrentRoom, sightingId);
  const cards = galleryGrid.querySelectorAll('.gallery-card');
  cards.forEach((card, i) => {
    const s = currentSightings[i];
    if (s && s.id === sightingId) {
      const btn = card.querySelector('.gallery-card-like');
      if (btn) {
        btn.classList.toggle('is-liked', liked);
        const icon = btn.querySelector('.gallery-card-like-icon');
        if (icon) icon.src = `/icons/${liked ? 'heart-full' : 'heart'}.svg`;
      }
    }
  });
};

if (galleryModalLike) {
  galleryModalLike.addEventListener('click', async (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    const sighting = currentSightings[currentSightingIndex] || sharedSighting;
    if (!sighting || !galleryCurrentRoom) return;
    const nowLiked = await toggleLikeSighting(galleryCurrentRoom, sighting.id);
    galleryModalLike.classList.toggle('is-liked', nowLiked);
    const icon = galleryModalLike.querySelector('.gallery-modal-like-icon');
    if (icon) icon.src = `/icons/${nowLiked ? 'heart-full' : 'heart'}.svg`;
    syncCardLikeState(sighting.id);
  });
}

if (galleryModalPrev) {
  galleryModalPrev.addEventListener('click', (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    navigateGallery(-1);
  });
}

if (galleryModalNext) {
  galleryModalNext.addEventListener('click', (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    navigateGallery(1);
  });
}

if (galleryModal) {
  galleryModal.addEventListener('click', (event) => {
    if (event.target === galleryModal) {
      explodeOnClick(event);
      closeGalleryModal();
    }
  });
}

if (galleryPlayPause) {
  galleryPlayPause.addEventListener('click', (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    if (videoEnded) {
      videoEnded = false;
      galleryModalVideo.currentTime = 0;
      galleryModalVideo.play().catch(() => {});
    } else if (galleryModalVideo.paused) {
      galleryModalVideo.play().catch(() => {});
    } else {
      galleryModalVideo.pause();
    }
  });
}

if (gallerySpeed) {
  gallerySpeed.addEventListener('click', (e) => {
    e.stopPropagation();
    currentSpeedIndex = (currentSpeedIndex + 1) % SPEED_STEPS.length;
    const speed = SPEED_STEPS[currentSpeedIndex];
    galleryModalVideo.playbackRate = speed;
    gallerySpeed.textContent = `${speed}x`;
  });
}

galleryModalVideo.addEventListener('click', (e) => {
  explodeOnClick(e);
  if (galleryModalVideo.ended) {
    galleryModalVideo.currentTime = 0;
    galleryModalVideo.play().catch(() => {});
  } else if (galleryModalVideo.paused) {
    galleryModalVideo.play().catch(() => {});
  } else {
    galleryModalVideo.pause();
  }
});

galleryModalVideo.addEventListener('play', updatePlayPauseIcon);
galleryModalVideo.addEventListener('pause', updatePlayPauseIcon);
galleryModalVideo.addEventListener('ended', updatePlayPauseIcon);

let videoInitialLoad = false;

const clearVideoLoadingState = () => {
  hideModalSpinner();
  const w = galleryModalVideo.parentElement;
  w.classList.remove('is-video-loading');
  w.style.aspectRatio = '';
  videoInitialLoad = false;
};

galleryModalVideo.addEventListener('timeupdate', () => {
  if (videoInitialLoad) {
    clearVideoLoadingState();
  }
});

galleryModalVideo.addEventListener('playing', () => {
  if (!videoInitialLoad) {
    clearVideoLoadingState();
  }
});

galleryModalVideo.addEventListener('waiting', showModalSpinner);

galleryModalImage.addEventListener('load', () => {
  if (galleryModalImage.style.display !== 'none') hideModalSpinner();
});
galleryModalImage.addEventListener('error', () => {
  if (galleryModalImage.style.display !== 'none') hideModalSpinner();
});

const updateGalleryVideoTime = () => {
  if (!galleryModalVideoTime) return;
  const fallbackDur = galleryModalVideo.duration;
  const dur = Number.isFinite(currentModalVideoDuration) ? currentModalVideoDuration : fallbackDur;
  const curr = galleryModalVideo.currentTime;
  const durStr = Number.isFinite(dur) && dur >= 0 ? formatDuration(dur) : '0:00';
  const currStr = Number.isFinite(curr) && curr >= 0 ? formatDuration(curr) : '0:00';
  galleryModalVideoTime.textContent = `${currStr} / ${durStr}`;
};

galleryModalVideo.addEventListener('loadedmetadata', updateGalleryVideoTime);
galleryModalVideo.addEventListener('durationchange', updateGalleryVideoTime);

galleryModalVideo.addEventListener('timeupdate', () => {
  if (isDraggingProgress) return;
  const dur = Number.isFinite(currentModalVideoDuration) ? currentModalVideoDuration : galleryModalVideo.duration;
  if (!dur) return;
  const pct = (galleryModalVideo.currentTime / dur) * 100;
  galleryProgressFill.style.width = pct + '%';
  galleryProgressThumb.style.left = pct + '%';
  updateGalleryVideoTime();
});

if (galleryProgress) {
  const seekTo = (e) => {
    const rect = galleryProgress.getBoundingClientRect();
    let pct = (e.clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));
    galleryProgressFill.style.width = (pct * 100) + '%';
    galleryProgressThumb.style.left = (pct * 100) + '%';
    return pct;
  };

  galleryProgress.addEventListener('pointerdown', (e) => {
    explodeOnClick(e);
    isDraggingProgress = true;
    galleryProgress.classList.add('is-dragging');
    galleryProgress.setPointerCapture(e.pointerId);
    const pct = seekTo(e);
    if (galleryModalVideo.duration) {
      galleryModalVideo.currentTime = pct * galleryModalVideo.duration;
      updateGalleryVideoTime();
    }
  });

  galleryProgress.addEventListener('pointermove', (e) => {
    if (!isDraggingProgress) return;
    const pct = seekTo(e);
    if (galleryModalVideo.duration) {
      galleryModalVideo.currentTime = pct * galleryModalVideo.duration;
      updateGalleryVideoTime();
    }
  });

  galleryProgress.addEventListener('pointerup', () => {
    isDraggingProgress = false;
    galleryProgress.classList.remove('is-dragging');
  });

  galleryProgress.addEventListener('pointercancel', () => {
    isDraggingProgress = false;
    galleryProgress.classList.remove('is-dragging');
  });
}

document.addEventListener('keydown', (event) => {
  if (!galleryModal?.classList.contains('active')) return;
  
  if (event.key === 'Escape') {
    closeGalleryModal();
  } else if (event.key === 'ArrowLeft') {
    navigateGallery(-1);
  } else if (event.key === 'ArrowRight') {
    navigateGallery(1);
  }
});


let galleryVideoObserver = null;

const setupGalleryVideoLazyLoad = () => {
  if (galleryVideoObserver) return;
  galleryVideoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const card = entry.target;
        galleryVideoObserver?.unobserve(card);
        const vid = card.querySelector('video.gallery-thumb[data-src]');
        if (!vid?.dataset.src) continue;
        const url = vid.dataset.src;
        delete vid.dataset.src;
        vid.src = url;
      }
    },
    { rootMargin: '100px', threshold: 0.01 }
  );
};

const observeGalleryVideoCard = (card) => {
  const vid = card.querySelector('video.gallery-thumb[data-src]');
  if (vid && galleryVideoObserver) {
    galleryVideoObserver.observe(card);
  }
};

const formatDurationBadge = (seconds) => {
  if (seconds == null || !isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const createGalleryCard = (sighting, index, animIndex) => {
  const ai = animIndex !== undefined ? animIndex : index;
  const card = document.createElement('article');
  card.className = 'gallery-card gallery-card--animate';
  card.style.animationDelay = `${ai * 40}ms`;
  card.setAttribute('tabindex', '0');
  card.ariaLabel = `Cat sighting from ${formatSightingDate(sighting.created_at)}`;

  const video = isVideoUrl(sighting.url);
  const hasThumbnail = video && sighting.thumbnail_url;

  if (hasThumbnail) {
    const durationStr = formatDurationBadge(sighting.duration);
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner">
          <div class="spinner spinner--thumb"></div>
        </div>
        <img class="gallery-thumb" src="${escapeHTML(sighting.thumbnail_url)}" alt="Cat sighting" loading="lazy">
        <div class="gallery-thumb-play">▶ <span class="gallery-thumb-duration">${escapeHTML(durationStr)}</span></div>
      </div>
      <div class="gallery-card-date">${escapeHTML(formatShortTime(sighting.created_at))}</div>
    `;

    const img = card.querySelector('img.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');

    img.onload = () => {
      spinner.classList.add('is-hidden');
    };

    img.onerror = () => {
      spinner.classList.add('is-hidden');
      img.src = PREVIEW_FALLBACK_POSTER;
      img.classList.add('gallery-thumb--fallback');
    };
  } else if (video) {
    const durationStr = formatDurationBadge(sighting.duration);
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner">
          <div class="spinner spinner--thumb"></div>
        </div>
        <video class="gallery-thumb" data-src="${escapeHTML(sighting.url)}" preload="metadata" muted playsinline></video>
        <div class="gallery-thumb-play">▶ <span class="gallery-thumb-duration">${escapeHTML(durationStr)}</span></div>
      </div>
      <div class="gallery-card-date">${escapeHTML(formatShortTime(sighting.created_at))}</div>
    `;

    const vid = card.querySelector('video.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');
    const badge = card.querySelector('.gallery-thumb-duration');

    vid.addEventListener('loadedmetadata', () => {
      vid.currentTime = Math.min(2, vid.duration);
      if (!durationStr && vid.duration != null && isFinite(vid.duration)) {
        if (badge) badge.textContent = formatDurationBadge(vid.duration);
      }
    });

    vid.addEventListener('seeked', () => {
      spinner.classList.add('is-hidden');
    }, { once: true });

    vid.onerror = () => {
      spinner.classList.add('is-hidden');
    };

    setupGalleryVideoLazyLoad();
    observeGalleryVideoCard(card);
  } else {
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner">
          <div class="spinner spinner--thumb"></div>
        </div>
        <img class="gallery-thumb" src="${escapeHTML(sighting.url)}" alt="Cat sighting" loading="lazy">
      </div>
      <div class="gallery-card-date">${escapeHTML(formatShortTime(sighting.created_at))}</div>
    `;

    const img = card.querySelector('.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');

    img.onload = () => {
      spinner.classList.add('is-hidden');
    };

    img.onerror = () => {
      spinner.classList.add('is-hidden');
      img.src = PREVIEW_FALLBACK_POSTER;
      img.classList.add('gallery-thumb--fallback');
    };
  }

  // Like button (room comes from galleryCurrentRoom when cards are created)
  const room = galleryCurrentRoom;
  const liked = room ? isSightingLiked(room, sighting.id) : false;
  const likeBtn = document.createElement('button');
  likeBtn.className = 'gallery-card-like' + (liked ? ' is-liked' : '');
  likeBtn.setAttribute('aria-label', 'Like');
  likeBtn.innerHTML = `<img class="gallery-card-like-icon" src="/icons/${liked ? 'heart-full' : 'heart'}.svg" alt="" aria-hidden="true">`;
  card.appendChild(likeBtn);

  likeBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    explodeOnClick(e);
    if (!room) return;
    const nowLiked = await toggleLikeSighting(room, sighting.id);
    likeBtn.classList.toggle('is-liked', nowLiked);
    likeBtn.querySelector('.gallery-card-like-icon').src = `/icons/${nowLiked ? 'heart-full' : 'heart'}.svg`;
    updateModalLikeState();
  });

  const openModal = () => {
    openGalleryModal(index);
  };

  card.addEventListener('click', openModal);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openModal();
    }
  });

  return card;
};

const setupGalleryInfiniteScroll = () => {
  if (galleryScrollHandler) {
    window.removeEventListener('scroll', galleryScrollHandler);
  }

  galleryScrollHandler = async () => {
    if (galleryIsLoadingMore || galleryCurrentPage >= galleryTotalPages) return;

    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    if (scrollTop + clientHeight >= scrollHeight - 300) {
      await loadMoreGallerySightings();
    }
  };

  window.addEventListener('scroll', galleryScrollHandler);
};

let galleryStickyResizeHandler = null;

const cleanupGalleryVideoObserver = () => {
  if (galleryVideoObserver) {
    galleryVideoObserver.disconnect();
    galleryVideoObserver = null;
  }
};

const cleanupGalleryScroll = () => {
  if (galleryScrollHandler) {
    window.removeEventListener('scroll', galleryScrollHandler);
    galleryScrollHandler = null;
  }
  if (galleryStickyResizeHandler) {
    window.removeEventListener('resize', galleryStickyResizeHandler);
    galleryStickyResizeHandler = null;
  }
  cleanupGalleryVideoObserver();
};

const buildGalleryUrl = (page) => {
  if (galleryLikedOnly && isLoggedIn()) {
    const user = getBackendUser();
    if (user?.id) {
      const params = new URLSearchParams();
      params.set('page', page);
      if (gallerySortOrder === 'oldest') params.set('order_by', 'oldest');
      if (galleryCurrentRoom) params.set('room', galleryCurrentRoom);
      return `${API_URL}/users/${user.id}/liked_cat_sightings?${params.toString()}`;
    }
  }
  const params = new URLSearchParams();
  params.set('page', page);
  if (gallerySortOrder === 'oldest') {
    params.set('order_by', 'oldest');
  }
  if (galleryLikedOnly && galleryCurrentRoom) {
    const liked = getLikedSightings(galleryCurrentRoom);
    liked.forEach(id => params.append('ids[]', id));
  }
  return `${API_URL}/rooms/${galleryCurrentRoom}/cat_sightings?${params.toString()}`;
};

const loadMoreGallerySightings = async () => {
  if (galleryIsLoadingMore || galleryCurrentPage >= galleryTotalPages || !galleryCurrentRoom) return;
  
  galleryIsLoadingMore = true;
  galleryLoading.style.display = 'flex';
  const loadVersion = galleryLoadVersion;
  
  try {
    const nextPage = galleryCurrentPage + 1;
    const res = await fetch(buildGalleryUrl(nextPage));
    const data = await res.json();
    if (loadVersion !== galleryLoadVersion) return;
    
    if (data.sightings && data.sightings.length > 0) {
      const startIndex = currentSightings.length;
      currentSightings = [...currentSightings, ...data.sightings];
      galleryCurrentPage = data.page;

      const lastGroup = galleryGrid.querySelector('.gallery-day-group:last-child');
      if (lastGroup) {
        lastRenderedDay = lastGroup.dataset.dayKey || null;
        currentDayCards = lastGroup.querySelector('.gallery-day-cards');
      }
      
      data.sightings.forEach((sighting, i) => {
        const dayKey = getDayKey(sighting.created_at);
        if (dayKey !== lastRenderedDay) {
          const { group, cards } = createDayGroup(sighting.created_at);
          galleryGrid.appendChild(group);
          currentDayCards = cards;
          lastRenderedDay = dayKey;
        }
        const card = createGalleryCard(sighting, startIndex + i, i);
        currentDayCards.appendChild(card);
      });
    }
  } catch (err) {
    console.error('Error loading more sightings:', err);
  } finally {
    galleryIsLoadingMore = false;
    galleryLoading.style.display = 'none';
  }
};

async function loadGallery() {
  stopLandingDataRefresh();
  cleanupGalleryScroll();
  
  const thisLoad = ++galleryLoadVersion;

  const params = router.getParams();
  const roomName = params.room_name;

  if (!roomName) {
    console.error('No room name provided');
    router.navigate('/');
    return;
  }

  if (!streams_list) {
    try {
      await waitForAuth();
      const token = await getToken();
      const res = await fetch(API_URL + '/rooms', token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
      if (thisLoad !== galleryLoadVersion) return;
      streams_list = await res.json();
    } catch (e) { /* proceed without stream info */ }
  }
  if (thisLoad !== galleryLoadVersion) return;

  showView('gallery');
  updateGalleryStickyTop();
  if (galleryStickyResizeHandler) window.removeEventListener('resize', galleryStickyResizeHandler);
  galleryStickyResizeHandler = updateGalleryStickyTop;
  window.addEventListener('resize', galleryStickyResizeHandler);
  galleryCurrentRoom = roomName;

  let stream = streams_list?.rooms?.find(s => s.name === roomName);
  if (!stream) {
    try {
      const res = await fetch(API_URL + '/rooms/' + roomName);
      if (thisLoad !== galleryLoadVersion) return;
      if (res.ok) stream = await res.json();
    } catch (e) { /* proceed without stream info */ }
  }
  if (thisLoad !== galleryLoadVersion) return;
  const displayName = stream?.display_name || roomName;

  if (galleryRoomName) {
    galleryRoomName.textContent = displayName;
  }

  if (stream) {
    const titleEl = document.getElementById('infoModalTitle');
    const roomNameEl = document.getElementById('infoModalRoomName');
    const descEl = document.getElementById('infoModalDescription');
    const statsEl = document.querySelector('.info-modal-stats');
    if (titleEl) titleEl.textContent = stream.display_name || 'Stream Info';
    if (roomNameEl) {
      if (stream.name) {
        roomNameEl.textContent = `/${stream.name}`;
        roomNameEl.removeAttribute('hidden');
      } else {
        roomNameEl.textContent = '';
        roomNameEl.hidden = true;
      }
    }
    if (descEl) {
      descEl.innerHTML = (stream.description && stream.description.trim())
        ? stream.description.trim()
        : '<p><i>No description available.</i></p>';
    }
    if (statsEl) {
      let creationDateStr = 'Unknown';
      if (stream.creation_date) {
        try {
          const datePart = stream.creation_date.replace(' ', 'T');
          const d = new Date(datePart.endsWith('Z') ? datePart : datePart + 'Z');
          creationDateStr = d.toLocaleDateString(DATE_LOCALE, { year: 'numeric', month: 'long', day: 'numeric' });
        } catch (e) { creationDateStr = stream.creation_date; }
      }
      let kibbleAmountStr = '';
      if (stream.times_fed !== undefined) {
        kibbleAmountStr = (stream.times_fed * 5 / 1000).toFixed(2).replace(/\.00$/, '');
      }
      const locationParts = [stream.city, stream.country]
        .map((x) => (typeof x === 'string' ? x.trim() : ''))
        .filter(Boolean);
      const locationStr = locationParts.join(', ');
      const locationLine = locationStr
        ? `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Located in <strong>${locationStr}</strong></div>`
        : '';
      statsEl.innerHTML =
        `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> About <strong>${kibbleAmountStr}kg</strong> of kibble dispensed</div>` +
        `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Operating since <strong>${creationDateStr}</strong></div>` +
        locationLine;
    }
  }

  // Sync filter bar active state
  const filterBar = document.getElementById('galleryFilterBar');
  if (filterBar) {
    filterBar.querySelectorAll('.gallery-sort-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.sort === gallerySortOrder);
    });
    const likedToggle = filterBar.querySelector('.gallery-liked-toggle');
    if (likedToggle) {
      likedToggle.classList.toggle('active', galleryLikedOnly);
      const heartIcon = likedToggle.querySelector('.gallery-filter-heart-icon');
      if (heartIcon) heartIcon.src = `/icons/${galleryLikedOnly ? 'heart-full' : 'heart'}.svg`;
    }
  }

  galleryGrid.innerHTML = '';
  galleryEmpty.style.display = 'none';
  galleryLoading.style.display = 'flex';
  currentSightings = [];
  galleryCurrentPage = 1;
  galleryTotalPages = 1;
  galleryTotalSightings = 0;

  if (isLoggedIn()) {
    await fetchUserLikedIds();
    if (thisLoad !== galleryLoadVersion) return;
  }

  try {
    if (galleryLikedOnly && !isLoggedIn() && galleryCurrentRoom && getLikedSightings(galleryCurrentRoom).length === 0) {
      galleryLoading.style.display = 'none';
      galleryEmpty.style.display = 'flex';
      galleryEmpty.querySelector('p').textContent = 'No liked videos yet. Tap the heart on a video to like it!';
      await openSharedImage();
      return;
    }

    const res = await fetch(buildGalleryUrl(1));
    if (thisLoad !== galleryLoadVersion) return;
    const data = await res.json();
    
    galleryLoading.style.display = 'none';

    if (!data.sightings || data.sightings.length === 0) {
      galleryEmpty.style.display = 'flex';
      galleryEmpty.querySelector('p').textContent = galleryLikedOnly
        ? 'No liked videos yet. Tap the heart on a video to like it!'
        : 'No cat sightings yet for this room.';
      await openSharedImage();
      return;
    }

    currentSightings = data.sightings;
    galleryCurrentPage = data.page || 1;
    galleryTotalPages = data.pages || 1;
    galleryTotalSightings = data.total || data.sightings.length;
    lastRenderedDay = null;
    currentDayCards = null;

    data.sightings.forEach((sighting, index) => {
      const dayKey = getDayKey(sighting.created_at);
      if (dayKey !== lastRenderedDay) {
        const { group, cards } = createDayGroup(sighting.created_at);
        galleryGrid.appendChild(group);
        currentDayCards = cards;
        lastRenderedDay = dayKey;
      }
      const card = createGalleryCard(sighting, index);
      currentDayCards.appendChild(card);
    });

    if (galleryTotalPages > 1) {
      setupGalleryInfiniteScroll();
    }

    await openSharedImage();

  } catch (err) {
    console.error('Error loading gallery:', err);
    galleryLoading.style.display = 'none';
    galleryEmpty.style.display = 'flex';
    galleryEmpty.querySelector('p').textContent = 'Failed to load gallery. Please try again later.';
  }
}

const galleryBackLink = galleryView?.querySelector('.gallery-back-link');
if (galleryBackLink) {
  const navigateBack = () => {
    if (galleryCurrentRoom) {
      router.navigate(`/${galleryCurrentRoom}`);
    } else {
      router.navigate('/');
    }
  };
  galleryBackLink.addEventListener('click', navigateBack);
  galleryBackLink.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateBack();
    }
  });
}

const infoModal = document.getElementById('infoModal');
const infoModalContent = infoModal?.querySelector('.info-modal-content');
const closeModalBtn = document.getElementById('closeModal');

function switchGalleryInfoTab(targetId) {
  if (!infoModal) return;
  infoModal.querySelectorAll('.modal-tab').forEach(t => t.classList.remove('active'));
  infoModal.querySelectorAll('.tab-content').forEach(c => {
    c.classList.remove('active');
    c.style.display = 'none';
  });
  const tab = infoModal.querySelector(`.modal-tab[data-target="${targetId}"]`);
  const content = infoModal.querySelector(`#${targetId}`);
  if (tab) tab.classList.add('active');
  if (content) {
    content.classList.add('active');
    content.style.display = 'flex';
  }
}

function openGalleryInfoModal() {
  if (!infoModal || !infoModalContent) return;
  if (infoModal.classList.contains('active')) return;
  infoModal.classList.add('active');
  modalOpen();
  infoModalContent.style.display = 'flex';
  switchGalleryInfoTab('infoTabContent');
}

function closeGalleryInfoModal() {
  if (!infoModal || !infoModalContent) return;
  if (!infoModal.classList.contains('active')) return;
  infoModal.classList.remove('active');
  modalClose();
  infoModalContent.style.display = 'none';
}

if (galleryInfoBtn) {
  galleryInfoBtn.addEventListener('click', openGalleryInfoModal);
  galleryInfoBtn.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openGalleryInfoModal();
    }
  });
}

if (closeModalBtn) {
  closeModalBtn.addEventListener('click', closeGalleryInfoModal);
}
if (infoModal) {
  infoModal.addEventListener('click', (e) => {
    if (e.target === infoModal) closeGalleryInfoModal();
  });
  infoModal.querySelectorAll('.modal-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.getAttribute('data-target');
      if (target) switchGalleryInfoTab(target);
    });
  });
}

// Gallery filter bar
const galleryFilterBar = document.getElementById('galleryFilterBar');
if (galleryFilterBar) {
  galleryFilterBar.addEventListener('click', (e) => {
    const sortBtn = e.target.closest('.gallery-sort-btn');
    if (sortBtn) {
      const sort = sortBtn.dataset.sort;
      if (sort === gallerySortOrder) return;
      gallerySortOrder = sort;
      galleryFilterBar.querySelectorAll('.gallery-sort-btn').forEach(b => b.classList.remove('active'));
      sortBtn.classList.add('active');
      loadGallery();
      return;
    }
    const likedBtn = e.target.closest('.gallery-liked-toggle');
    if (likedBtn) {
      galleryLikedOnly = !galleryLikedOnly;
      likedBtn.classList.toggle('active', galleryLikedOnly);
      const heartIcon = likedBtn.querySelector('.gallery-filter-heart-icon');
      if (heartIcon) heartIcon.src = `/icons/${galleryLikedOnly ? 'heart-full' : 'heart'}.svg`;
      loadGallery();
    }
  });
}

// Pull-to-refresh setup
initPullToRefresh();
initGalleryPullToRefresh();

// Set up routes
router.addRoute('/', loadLivestreams);
router.addRoute('/privacy', () => {
  showView('privacy');
});
router.addRoute('/:slug', loadLivestream);
router.addRoute('/:room_name/gallery', loadGallery);
router.addRoute('/:room_name/gallery/:sighting_id', loadGallery);

// Backwards compatibility: redirect old /rooms/... URLs to new routes
router.addRoute('/rooms/:slug', () => {
  const { slug } = router.getParams();
  history.replaceState(null, '', `/${slug}`);
  loadLivestream();
});
router.addRoute('/rooms/:room_name/gallery', () => {
  const { room_name } = router.getParams();
  history.replaceState(null, '', `/${room_name}/gallery`);
  loadGallery();
});
router.addRoute('/rooms/:room_name/gallery/:sighting_id', () => {
  const { room_name, sighting_id } = router.getParams();
  history.replaceState(null, '', `/${room_name}/gallery/${sighting_id}`);
  loadGallery();
});

// Run initial route after routes are registered (fixes tabs not showing on PWA first launch)
router.runInitialRoute();